// Company & agency licenses — Darkspire Risk LLC's own regulatory standing.
// A filterable table of held company_licenses (status, service line) plus a
// "Targets" card listing jurisdiction_targets we operate in / are pursuing.
// Rows link to /compliance/licenses/:id. Create modal pulls its license-type
// and qualifying-agent option lists from the catalog hooks.
//
// NOTE: route + nav link for this page are wired in App.tsx / NavMenu during
// the Integration pass.

import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type {
  CompanyLicense,
  CompanyLicenseStatus,
  ComplianceLicenseType,
  JurisdictionTarget,
  QualifyingAgent,
  ServiceLine,
  TargetStatus,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import { ApiError } from '../api/client';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  FormField,
  FormRow,
  Input,
  Select,
  Table,
  Textarea,
  type BadgeTone,
  type SelectOption,
} from '../components/ui';
import {
  useCompanyLicenses,
  useCreateCompanyLicense,
  useLicenseTypes,
  useQualifyingAgents,
  useTargets,
  useCreateTarget,
  useUpdateTarget,
  useDeleteTarget,
  type CompanyLicenseFilters,
} from '../api/queries/compliance';

// ---------- Static option lists ----------

const SERVICE_LINES: { value: ServiceLine; label: string }[] = [
  { value: 'unarmed_guard', label: 'Unarmed guard' },
  { value: 'armed_guard', label: 'Armed guard' },
  { value: 'executive_protection', label: 'Executive protection' },
  { value: 'private_investigation', label: 'Private investigation' },
];

const LICENSE_STATUSES: { value: CompanyLicenseStatus; label: string }[] = [
  { value: 'applied', label: 'Applied' },
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'expired_grace', label: 'In grace period' },
  { value: 'lapsed', label: 'Lapsed' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'surrendered', label: 'Surrendered' },
];

const TARGET_STATUSES: { value: TargetStatus; label: string }[] = [
  { value: 'operating', label: 'Operating' },
  { value: 'pursuing', label: 'Pursuing' },
  { value: 'evaluating', label: 'Evaluating' },
  { value: 'declined', label: 'Declined' },
];

// ---------- Helpers ----------

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 403) {
    return 'You do not have permission to do that.';
  }
  return err instanceof ApiError ? err.message : fallback;
}

function fmtDate(epochMs: number | null | undefined): string {
  return epochMs === null || epochMs === undefined
    ? '—'
    : new Date(epochMs).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      });
}

function licenseStatusTone(status: CompanyLicenseStatus): BadgeTone {
  switch (status) {
    case 'active':
      return 'success';
    case 'applied':
    case 'pending':
    case 'expired_grace':
      return 'warn';
    case 'lapsed':
    case 'suspended':
    case 'revoked':
    case 'surrendered':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function licenseStatusLabel(status: CompanyLicenseStatus): string {
  return LICENSE_STATUSES.find((s) => s.value === status)?.label ?? status;
}

function serviceLineLabel(line: ServiceLine): string {
  return SERVICE_LINES.find((s) => s.value === line)?.label ?? line;
}

function targetStatusTone(status: TargetStatus): BadgeTone {
  switch (status) {
    case 'operating':
      return 'success';
    case 'pursuing':
    case 'evaluating':
      return 'warn';
    case 'declined':
      return 'neutral';
    default:
      return 'neutral';
  }
}

function targetStatusLabel(status: TargetStatus): string {
  return TARGET_STATUSES.find((s) => s.value === status)?.label ?? status;
}

// =============================================================================
// Page
// =============================================================================

export function CompanyLicensesPage(): JSX.Element {
  const navigate = useNavigate();
  const canWrite = can('compliance.write');

  // Filter sentinels: Radix Select forbids an empty value, so 'all' means
  // "no filter" and is dropped from the query below.
  const [statusFilter, setStatusFilter] = useState<CompanyLicenseStatus | 'all'>('all');
  const [serviceLineFilter, setServiceLineFilter] = useState<ServiceLine | 'all'>('all');
  const [createOpen, setCreateOpen] = useState(false);

  const filters: CompanyLicenseFilters = useMemo(
    () => ({
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(serviceLineFilter !== 'all' ? { service_line: serviceLineFilter } : {}),
    }),
    [statusFilter, serviceLineFilter],
  );

  const licensesQuery = useCompanyLicenses(filters);
  const licenseTypesQuery = useLicenseTypes();
  const qaQuery = useQualifyingAgents();

  const licenses = licensesQuery.data?.licenses ?? [];
  const licenseTypes = licenseTypesQuery.data?.license_types ?? [];
  const qualifyingAgents = qaQuery.data?.qualifying_agents ?? [];

  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...LICENSE_STATUSES.map((s) => ({ value: s.value, label: s.label })),
  ];
  const serviceLineFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All service lines' },
    ...SERVICE_LINES.map((s) => ({ value: s.value, label: s.label })),
  ];

  return (
    <section className="compliance-page">
      <header className="page-header">
        <div>
          <h2>Company licenses</h2>
          <p className="page-sub">
            Darkspire Risk LLC&rsquo;s own agency licenses and regulatory standing.
          </p>
        </div>
        {canWrite && (
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            New license
          </Button>
        )}
      </header>

      <div className="page-toolbar">
        <label className="form-field inline">
          <span>Status</span>
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as CompanyLicenseStatus | 'all')}
            options={statusFilterOptions}
            aria-label="Filter by status"
          />
        </label>

        <label className="form-field inline">
          <span>Service line</span>
          <Select
            value={serviceLineFilter}
            onValueChange={(v) => setServiceLineFilter(v as ServiceLine | 'all')}
            options={serviceLineFilterOptions}
            aria-label="Filter by service line"
          />
        </label>
      </div>

      {licensesQuery.isPending && <p className="muted">Loading licenses…</p>}
      {licensesQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(licensesQuery.error, 'Failed to load licenses.')}
        </p>
      )}
      {!licensesQuery.isPending && !licensesQuery.isError && licenses.length === 0 && (
        <p className="muted empty-state">
          No licenses {statusFilter !== 'all' || serviceLineFilter !== 'all' ? 'match these filters' : 'recorded yet'}.
        </p>
      )}

      {licenses.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>License #</Table.HeaderCell>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Jurisdiction</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Expires</Table.HeaderCell>
              <Table.HeaderCell>Renewal due</Table.HeaderCell>
              <Table.HeaderCell>Qualifying agent</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {licenses.map((l: CompanyLicense) => (
              <Table.Row
                key={l.id}
                interactive
                onClick={() => navigate(`/compliance/licenses/${l.id}`)}
                tabIndex={0}
                role="link"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/compliance/licenses/${l.id}`);
                }}
              >
                <Table.Cell tone="mono">{l.license_number ? <code>{l.license_number}</code> : '—'}</Table.Cell>
                <Table.Cell tone="strong">{l.license_type_display ?? l.license_type_code ?? `Type #${l.license_type_id}`}</Table.Cell>
                <Table.Cell>{l.jurisdiction_name ?? l.jurisdiction_code ?? '—'}</Table.Cell>
                <Table.Cell>
                  <Badge tone={licenseStatusTone(l.status)}>
                    {licenseStatusLabel(l.status)}
                  </Badge>
                </Table.Cell>
                <Table.Cell>{fmtDate(l.expiration_date)}</Table.Cell>
                <Table.Cell>{fmtDate(l.renewal_due_date)}</Table.Cell>
                <Table.Cell tone="muted">{l.qualifying_agent_name ?? '—'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      <TargetsCard />

      {createOpen && (
        <NewLicenseModal
          licenseTypes={licenseTypes}
          qualifyingAgents={qualifyingAgents}
          onClose={() => setCreateOpen(false)}
        />
      )}
    </section>
  );
}

// =============================================================================
// Targets card — jurisdiction_targets list with add / edit / delete
// =============================================================================

function TargetsCard(): JSX.Element {
  const canWrite = can('compliance.write');
  const canDelete = can('compliance.delete');

  const targetsQuery = useTargets();
  const deleteTarget = useDeleteTarget();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<JurisdictionTarget | null>(null);
  const [deleting, setDeleting] = useState<JurisdictionTarget | null>(null);

  const targets = targetsQuery.data?.targets ?? [];

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteTarget.mutateAsync(deleting.id);
      toast.success('Target removed.');
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to remove target.'));
    }
  };

  return (
    <Card className="compliance-card">
      <Card.Header>
        <div>
          <Card.Title>Targets</Card.Title>
          <p className="page-sub">Jurisdictions we operate in or are pursuing.</p>
        </div>
        {canWrite && (
          <Button variant="ghost" onClick={() => setCreateOpen(true)}>
            Add target
          </Button>
        )}
      </Card.Header>

      <Card.Body>
        {targetsQuery.isPending && <p className="muted">Loading targets…</p>}
        {targetsQuery.isError && (
          <p className="error-text" role="alert">
            {errMessage(targetsQuery.error, 'Failed to load targets.')}
          </p>
        )}
        {!targetsQuery.isPending && !targetsQuery.isError && targets.length === 0 && (
          <p className="muted empty-state">No jurisdiction targets yet.</p>
        )}

        {targets.length > 0 && (
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell>Jurisdiction</Table.HeaderCell>
                <Table.HeaderCell>Service line</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Priority</Table.HeaderCell>
                {(canWrite || canDelete) && <Table.HeaderCell>Actions</Table.HeaderCell>}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {targets.map((t: JurisdictionTarget) => (
                <Table.Row key={t.id}>
                  <Table.Cell tone="strong">{t.jurisdiction_name ?? t.jurisdiction_code ?? `Jurisdiction #${t.jurisdiction_id}`}</Table.Cell>
                  <Table.Cell>{serviceLineLabel(t.service_line)}</Table.Cell>
                  <Table.Cell>
                    <Badge tone={targetStatusTone(t.status)}>
                      {targetStatusLabel(t.status)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell>{t.priority ?? '—'}</Table.Cell>
                  {(canWrite || canDelete) && (
                    <Table.Cell>
                      {canWrite && (
                        <Button variant="ghost" onClick={() => setEditing(t)}>
                          Edit
                        </Button>
                      )}
                      {canDelete && (
                        <Button variant="danger" onClick={() => setDeleting(t)}>
                          Delete
                        </Button>
                      )}
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Card.Body>

      {createOpen && <TargetModal mode="create" onClose={() => setCreateOpen(false)} />}
      {editing && <TargetModal mode="edit" target={editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        title="Remove target"
        message={deleting ? 'Remove this jurisdiction target?' : ''}
        confirmLabel="Remove"
        danger
        busy={deleteTarget.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </Card>
  );
}

interface TargetModalProps {
  mode: 'create' | 'edit';
  target?: JurisdictionTarget;
  onClose: () => void;
}

function TargetModal({ mode, target, onClose }: TargetModalProps): JSX.Element {
  const create = useCreateTarget();
  const update = useUpdateTarget();
  const busy = create.isPending || update.isPending;

  const [jurisdictionId, setJurisdictionId] = useState(
    target ? String(target.jurisdiction_id) : '',
  );
  const [serviceLine, setServiceLine] = useState<ServiceLine>(
    target?.service_line ?? 'unarmed_guard',
  );
  const [status, setStatus] = useState<TargetStatus>(target?.status ?? 'pursuing');
  const [priority, setPriority] = useState(
    target?.priority !== null && target?.priority !== undefined ? String(target.priority) : '',
  );
  const [notes, setNotes] = useState(target?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const jid = Number(jurisdictionId);
    if (!Number.isInteger(jid) || jid <= 0) {
      setError('A jurisdiction id is required.');
      return;
    }
    const pri = priority.trim() === '' ? undefined : Number(priority);
    try {
      if (mode === 'create') {
        await create.mutateAsync({
          jurisdiction_id: jid,
          service_line: serviceLine,
          status,
          ...(pri !== undefined ? { priority: pri } : {}),
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        });
        toast.success('Target added.');
      } else if (target) {
        await update.mutateAsync({
          id: target.id,
          jurisdiction_id: jid,
          service_line: serviceLine,
          status,
          priority: pri ?? null,
          notes: notes.trim() ? notes.trim() : null,
        });
        toast.success('Target updated.');
      }
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Could not save the target.'));
    }
  };

  return (
    <Dialog
      title={mode === 'create' ? 'Add target' : 'Edit target'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="target-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="target-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Jurisdiction id" required>
          <Input
            type="number"
            value={jurisdictionId}
            onChange={(e) => setJurisdictionId(e.target.value)}
            min={1}
            disabled={busy}
            required
            autoFocus
          />
        </FormField>
        <FormRow>
          <FormField label="Service line">
            <select
              className="form-input"
              value={serviceLine}
              onChange={(e) => setServiceLine(e.target.value as ServiceLine)}
              disabled={busy}
            >
              {SERVICE_LINES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Status">
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as TargetStatus)}
              disabled={busy}
            >
              {TARGET_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </FormField>
        </FormRow>
        <FormField label="Priority">
          <Input
            type="number"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            disabled={busy}
            placeholder="Optional — lower = higher priority"
          />
        </FormField>
        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={10_000}
            disabled={busy}
            placeholder="Optional"
          />
        </FormField>
        {error && <p className="error-text" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// =============================================================================
// New license modal
// =============================================================================

interface NewLicenseModalProps {
  licenseTypes: ComplianceLicenseType[];
  qualifyingAgents: QualifyingAgent[];
  onClose: () => void;
}

function NewLicenseModal({
  licenseTypes,
  qualifyingAgents,
  onClose,
}: NewLicenseModalProps): JSX.Element {
  const create = useCreateCompanyLicense();
  const busy = create.isPending;

  const [licenseTypeId, setLicenseTypeId] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [legalEntity, setLegalEntity] = useState('');
  const [dba, setDba] = useState('');
  const [status, setStatus] = useState<CompanyLicenseStatus>('pending');
  const [issueDate, setIssueDate] = useState<number | null>(null);
  const [expirationDate, setExpirationDate] = useState<number | null>(null);
  const [renewalDue, setRenewalDue] = useState<number | null>(null);
  const [qualifyingAgentId, setQualifyingAgentId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const ltId = Number(licenseTypeId);
    if (!Number.isInteger(ltId) || ltId <= 0) {
      setError('Select a license type.');
      return;
    }
    const qaId = qualifyingAgentId === '' ? undefined : Number(qualifyingAgentId);
    try {
      await create.mutateAsync({
        license_type_id: ltId,
        status,
        ...(licenseNumber.trim() ? { license_number: licenseNumber.trim() } : {}),
        ...(legalEntity.trim() ? { legal_entity_name: legalEntity.trim() } : {}),
        ...(dba.trim() ? { dba: dba.trim() } : {}),
        ...(issueDate !== null ? { issue_date: issueDate } : {}),
        ...(expirationDate !== null ? { expiration_date: expirationDate } : {}),
        ...(renewalDue !== null ? { renewal_due_date: renewalDue } : {}),
        ...(qaId !== undefined ? { qualifying_agent_id: qaId } : {}),
      });
      toast.success('License created.');
      onClose();
    } catch (err) {
      setError(errMessage(err, 'Could not create the license.'));
    }
  };

  return (
    <Dialog
      title="New company license"
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" form="new-license-form" variant="primary" disabled={busy}>
            {busy ? 'Saving…' : 'Create license'}
          </Button>
        </>
      }
    >
      <form id="new-license-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="License type" required>
          <select
            className="form-input"
            value={licenseTypeId}
            onChange={(e) => setLicenseTypeId(e.target.value)}
            disabled={busy}
            required
            autoFocus
          >
            <option value="">Select a license type…</option>
            {licenseTypes.map((lt) => (
              <option key={lt.id} value={lt.id}>
                {lt.display_name}
                {lt.jurisdiction_code ? ` (${lt.jurisdiction_code})` : ''}
              </option>
            ))}
          </select>
        </FormField>

        <FormRow>
          <FormField label="License number">
            <Input
              type="text"
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              maxLength={120}
              disabled={busy}
            />
          </FormField>
          <FormField label="Status">
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as CompanyLicenseStatus)}
              disabled={busy}
            >
              {LICENSE_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Legal entity name">
            <Input
              type="text"
              value={legalEntity}
              onChange={(e) => setLegalEntity(e.target.value)}
              maxLength={300}
              disabled={busy}
              placeholder="Darkspire Risk LLC"
            />
          </FormField>
          <FormField label="DBA">
            <Input
              type="text"
              value={dba}
              onChange={(e) => setDba(e.target.value)}
              maxLength={300}
              disabled={busy}
            />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Issue date">
            <DateInput value={issueDate} onChange={setIssueDate} disabled={busy} />
          </FormField>
          <FormField label="Expiration date">
            <DateInput value={expirationDate} onChange={setExpirationDate} disabled={busy} />
          </FormField>
        </FormRow>

        <FormRow>
          <FormField label="Renewal due">
            <DateInput value={renewalDue} onChange={setRenewalDue} disabled={busy} />
          </FormField>
          <FormField label="Qualifying agent">
            <select
              className="form-input"
              value={qualifyingAgentId}
              onChange={(e) => setQualifyingAgentId(e.target.value)}
              disabled={busy}
            >
              <option value="">None</option>
              {qualifyingAgents.map((qa) => (
                <option key={qa.id} value={qa.id}>
                  {qa.full_name ?? `QA #${qa.id}`}
                </option>
              ))}
            </select>
          </FormField>
        </FormRow>
        {error && <p className="error-text" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}
